import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { WizardState } from '../types/index'
import { supabase } from '../lib/supabase'
import { Step1 } from '../components/Step1'
import { Step2 } from '../components/Step2'
import { Step3 } from '../components/Step3'
import { Step4 } from '../components/Step4'
import { PremiumModal } from '../components/PremiumModal'

const initialState: WizardState = {
  step: 1,
  examCount: 1,
  restDays: { enabled: false, weekDays: [], specificDates: [] },
  studyMode: 'mixed',
  exams: [],
}

export function WizardPage({ user, isPremium, openPremium, onPremiumOpened }: { user: any, isPremium: boolean, openPremium?: boolean, onPremiumOpened?: () => void }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [state, setState] = useState<WizardState>(initialState)

  const logout = async () => { await supabase.auth.signOut() }
  const [showPremium, setShowPremium] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    if (openPremium && user) {
      setShowPremium(true)
      onPremiumOpened?.()
    }
  }, [openPremium, user])

  const updateState = (updates: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }

  const goNext = () => {

    if (state.step < 4) updateState({ step: state.step + 1 })
    else navigate('/plan', { state: { wizardState: state } })
  }

  const goBack = () => {
    if (state.step > 1) updateState({ step: state.step - 1 })
  }

  const isRTL = ['ar', 'ur', 'fa'].includes(i18n.language)

  return (
    <>
    <style>{`
      @media (max-width: 600px) {
        .wiz-desktop-nav { display: none !important; }
        .wiz-hamburger { display: flex !important; }
        .full-name { display: none; }
        .short-name { display: inline !important; }
      }
      .wiz-hamburger { display: none; }
      .wiz-dropdown { position: absolute; top: 56px; right: 0; left: 0; background: white; border-bottom: 1px solid #E5E7EB; z-index: 100; padding: 8px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
      .wiz-dropdown button { display: block; width: 100%; text-align: left; padding: 12px 20px; background: transparent; border: none; font-size: 15px; color: #111827; cursor: pointer; }
      .wiz-dropdown button:hover { background: #F9FAFB; }
      .wiz-dropdown .sep { height: 1px; background: #F3F4F6; margin: 4px 0; }
    `}</style>
    {showPremium && <PremiumModal onClose={() => setShowPremium(false)} user={user} />}
    <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <header style={{ backgroundColor: 'white', borderBottom: '1px solid #E5E7EB', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
<img src="/favicon.svg" alt="logo" style={{ width: 36, height: 36, flexShrink: 0 }} />
          <span style={{ fontWeight: 700, color: '#111827', fontSize: 15 }}>
            <span className="full-name">Study Planner Schedule</span>
            <span className="short-name" style={{ display: 'none' }}>Study Planner</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isPremium ? (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#15803D', backgroundColor: '#F0FDF4', padding: '6px 14px', borderRadius: 20, border: '1px solid #86EFAC', display: 'flex', alignItems: 'center', gap: 6 }}>
                ✦ Premium ✓
              </div>
            ) : (
              <button onClick={() => setShowPremium(true)} style={{ fontSize: 13, fontWeight: 500, color: '#D97706', backgroundColor: '#FFFBEB', padding: '6px 12px', borderRadius: 20, border: '1px solid #FDE68A', cursor: 'pointer' }}>
                ✦ {t('nav.premium')}
              </button>
            )}
            {user ? (
              <>
                <div className="wiz-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => window.location.href='/plans'} style={{ fontSize: 12, color: '#1c3fff', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{t('nav.plans')}</button>
                  <button onClick={() => window.location.href='/account'} style={{ fontSize: 12, color: '#6B7280', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>{t('nav.account')}</button>
                  <button onClick={logout} style={{ fontSize: 12, color: '#6B7280', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>{t('nav.logout')}</button>
                </div>
                <button className="wiz-hamburger" onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111827', padding: '4px 8px' }}>
                  {menuOpen ? '✕' : '☰'}
                </button>
              </>
            ) : (
              <a href="/auth" style={{ fontSize: 13, color: 'white', backgroundColor: '#1c3fff', textDecoration: 'none', fontWeight: 600, padding: '6px 14px', borderRadius: 20 }}>Accedi</a>
            )}
          </div>
      </header>

      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
      )}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
      )}
      {menuOpen && user && (
        <div className="wiz-dropdown" onClick={() => setMenuOpen(false)}>
          <div style={{ padding: '8px 20px 4px', fontSize: 12, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            {user.user_metadata?.name || user.email}
          </div>
          <div className="sep" />
          <button onClick={() => window.location.href='/plans'}>📚 I miei piani</button>
          <button onClick={() => window.location.href='/account'}>{t('nav.account')}</button>
          <div className="sep" />
          <button onClick={logout} style={{ color: '#EF4444' }}>{t('nav.logout')}</button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, paddingTop: 32, paddingBottom: 8 }}>
        {[1, 2, 3, 4].map(s => (
          <div key={s} style={{
            width: s === state.step ? 14 : 10,
            height: s === state.step ? 14 : 10,
            borderRadius: '50%',
            backgroundColor: s === state.step ? '#1c3fff' : s < state.step ? '#93C5FD' : '#E5E7EB',
            transition: 'all 0.2s'
          }} />
        ))}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        {state.step === 1 && <Step1 state={state} updateState={updateState} />}
        {state.step === 2 && <Step2 state={state} updateState={updateState} />}
        {state.step === 3 && <Step3 state={state} updateState={updateState} />}
        {state.step === 4 && <Step4 state={state} updateState={updateState} />}
      </div>

      <div style={{ backgroundColor: 'white', borderTop: '1px solid #E5E7EB', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={goBack} style={{ visibility: state.step === 1 ? 'hidden' : 'visible', color: '#4B5563', fontWeight: 500, padding: '8px 16px', borderRadius: 8, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: 15 }}>
          ← {t('nav.back')}
        </button>
        <button onClick={goNext} style={{ backgroundColor: '#1c3fff', color: 'white', fontWeight: 500, padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 15 }}>
          {state.step === 4 ? '✨ ' + t('step4.generatePlan') : t('nav.next') + ' →'}
        </button>
      </div>
    </div>
  </>
  )
}
